// S18B-2 — CMS Operator Dashboard (OSWC GAP-4).
//
// A single command-center view composed entirely of EXISTING data: the active
// alert queue (alert center), camera-health summary (e-Map health colours),
// on-duty operators (shift log), and recent events. No new analytics.
import { useState } from "react";

import { useAlertEvents, useAlertEventsSummary } from "@/api/alerts";
import { useEmapEntities } from "@/api/emap";
import { useEndShift, useShifts, useStartShift } from "@/api/cms";
import { useLicense } from "@/api/license";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const NAVY = "#263238";
const ACCENT = "#ff6d00";

export function CMSDashboard() {
  const license = useLicense();
  const enabled = license.data?.features?.["cms_dashboard"] ?? true;

  const summary = useAlertEventsSummary();
  const openAlerts = useAlertEvents({ status: "open", limit: 15 });
  const recent = useAlertEvents({ limit: 10 });
  const emap = useEmapEntities();
  const shifts = useShifts({ activeOnly: true });
  const startShift = useStartShift();
  const endShift = useEndShift();
  const [opName, setOpName] = useState("");

  if (!enabled) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          The CMS operator dashboard is not available on your current plan.
        </CardContent>
      </Card>
    );
  }

  const health = (emap.data?.entities ?? []).reduce(
    (acc, e) => {
      acc[e.health_status] = (acc[e.health_status] ?? 0) + 1;
      return acc;
    },
    { green: 0, amber: 0, red: 0, grey: 0 } as Record<string, number>,
  );
  const s = summary.data;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight" style={{ color: NAVY }}>
        Command Center
      </h1>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Open alerts" value={s?.total_open ?? 0} tone="red" />
        <Kpi label="Critical" value={s?.open?.critical ?? 0} tone="red" />
        <Kpi label="Sites green" value={health.green} tone="green" />
        <Kpi
          label="Sites amber/red"
          value={(health.amber ?? 0) + (health.red ?? 0)}
          tone="amber"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Active alert queue */}
        <Card className="lg:col-span-2">
          <CardContent className="p-4">
            <h2 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
              Active alert queue
            </h2>
            <div className="space-y-1">
              {(openAlerts.data?.items ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No open alerts.</p>
              ) : (
                (openAlerts.data?.items ?? []).map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between rounded border px-2 py-1 text-sm"
                  >
                    <span className="truncate">
                      <SeverityDot severity={a.severity} /> {a.title}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(a.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* On-duty operators */}
        <Card>
          <CardContent className="p-4">
            <h2 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
              On duty
            </h2>
            <div className="mb-3 flex gap-2">
              <Input
                value={opName}
                onChange={(e) => setOpName(e.target.value)}
                placeholder="Operator name"
              />
              <Button
                style={{ backgroundColor: ACCENT }}
                disabled={!opName || startShift.isPending}
                onClick={async () => {
                  await startShift.mutateAsync({ operator_name: opName });
                  setOpName("");
                }}
              >
                Clock in
              </Button>
            </div>
            <div className="space-y-1">
              {(shifts.data ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No one on duty.</p>
              ) : (
                (shifts.data ?? []).map((sh) => (
                  <div
                    key={sh.id}
                    className="flex items-center justify-between rounded border px-2 py-1 text-sm"
                  >
                    <span>
                      {sh.operator_name}
                      <span className="ml-1 text-xs text-muted-foreground">
                        since {new Date(sh.started_at).toLocaleTimeString()}
                      </span>
                    </span>
                    <button
                      className="text-xs text-red-600 hover:underline"
                      onClick={() => endShift.mutate(sh.id)}
                    >
                      clock out
                    </button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent events */}
      <Card>
        <CardContent className="p-4">
          <h2 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
            Recent events
          </h2>
          <div className="space-y-1">
            {(recent.data?.items ?? []).map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm">
                <span className="truncate">
                  <SeverityDot severity={a.severity} /> {a.title}
                  <span className="ml-1 text-xs text-muted-foreground">· {a.status}</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {new Date(a.created_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "red" | "green" | "amber";
}) {
  const color =
    tone === "red" ? "#ef4444" : tone === "green" ? "#10b981" : "#f59e0b";
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold" style={{ color }}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function SeverityDot({ severity }: { severity: string }) {
  const color =
    severity === "critical" ? "#ef4444" : severity === "warning" ? "#f59e0b" : "#3b82f6";
  return (
    <span
      className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
      style={{ backgroundColor: color }}
    />
  );
}

export default CMSDashboard;
